/**
 * Pure redirect-rule matcher. No Node / no Vue / no h3 deps so it runs in
 * three places that previously each carried byte-identical copies:
 *
 *   - `app/middleware/aa-redirects.global.ts` — client SPA navigation guard
 *   - `server/middleware/redirects.ts`        — Nitro dev SSR middleware
 *   - `server/handlers/redirects-json.ts`     — (indirect, via redirectsFile)
 *
 * Wildcard semantics ARE the contract — keep the test suite in sync if you
 * change them, otherwise SSR vs SPA navigation will silently diverge.
 *
 * Grammar:
 *   from: "/foo"        exact-string match
 *   from: "/foo/*"      matches "/foo", "/foo/", and "/foo/<rest>"
 *   to:   "/bar/:splat" the literal ":splat" token is stripped; sub-paths
 *                       get the matched remainder appended to the result
 *   to:   "/bar"        sub-paths also get the remainder appended —
 *                       `/foo/* → /bar` mapping `/foo/x` yields `/bar/x`
 *
 * Trailing-slash quirk: `/foo` and `/foo/` BOTH collapse to the bare target
 * (no trailing slash preserved for the base case). Only `/foo/<rest>` —
 * with at least one extra path segment — produces `/bar/<rest>`.
 */
export interface RedirectRule {
  from: string;
  to: string;
  status: number;
}

export type Matcher = (path: string) => string | null;

export function compile(rules: RedirectRule[]): Matcher[] {
  return rules.map((r) => {
    if (r.from.endsWith("/*")) {
      const base = r.from.slice(0, -2);
      const targetBase = r.to.replace("/:splat", "");
      return (path) => {
        if (path === base || path === base + "/") return targetBase;
        if (path.startsWith(base + "/")) return targetBase + path.slice(base.length);
        return null;
      };
    }
    return (path) => (path === r.from ? r.to : null);
  });
}
